import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import axios from 'axios';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';

import FilesService, { IUploadFileUrl } from '@services/FilesService';
import { env } from 'env';
import { useTranslation } from 'react-i18next';

import { useStyles } from './styles';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import EventsService from '@services/EventsService';

type CustomSpotsEditorProps = {
  open: boolean;
  onClose: () => void;
};

const CustomSpotsEditor: React.FC<CustomSpotsEditorProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    currentRoom,
    roomSpots,
    setRoomSpots,
    selectedCustomizationUeId,
    setSelectedCustomizationUeId,
    setShowCustomSpotsEditor,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const classes = useStyles();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedAsset, setUploadedAsset] = useState<IUploadFileUrl | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const resetState = useCallback(() => {
    setIsDragging(false);
    setIsUploading(false);
    setError(null);
    setUploadedAsset(null);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  }, [previewUrl]);

  const handleFiles = useCallback(
    async (files?: FileList | null) => {
      if (!files?.length) {
        return;
      }

      const [file] = Array.from(files);
      if (!file.type.startsWith('image/')) {
        setError(t('customSpotsEditor.errorUnsupported', 'Images only are supported'));
        return;
      }

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setError(null);
      setUploadedAsset(null);
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    },
    [previewUrl, t],
  );

  const handleSelectClick = () => {
    fileInputRef.current?.click();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleConfirm = useCallback(async () => {
    if (!selectedFile) return;
    if (!currentRoom) {
      setError(t('customSpotsEditor.errorNoRoom', 'Room is not available.'));
      return;
    }
    if (!selectedCustomizationUeId) {
      setError(
        t('customSpotsEditor.errorNoSelection', 'No spot selected for update.'),
      );
      return;
    }
    setIsUploading(true);
    setError(null);
    try {
      const [uploadInfo] = await FilesService.uploadFiles([selectedFile]);

      const spotMatch = roomSpots.find(
        (spot: any) => spot.ueIdentifier === selectedCustomizationUeId,
      );

      const dataPayload: any = {
        location: uploadInfo.location,
        url: uploadInfo.url,
      };
      if ((uploadInfo as any).md5) {
        dataPayload.md5 = (uploadInfo as any).md5;
      }

      const patchSpot = async () => {
        if (!spotMatch) return false;
        const spotId = spotMatch.roomSpotId || spotMatch.id;
        if (!spotId) return false;
        await axios.patch(
          `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${spotId}`,
          {
            data: dataPayload,
            isActive: true,
          },
        );
        return true;
      };

      let updatedViaPatch = false;
      try {
        updatedViaPatch = await patchSpot();
      } catch (patchError: any) {
        const status = patchError?.response?.status;
        if (status !== 404) {
          throw patchError;
        }
      }

      if (!updatedViaPatch) {
        await axios.post(
          `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots`,
          {
            name: selectedCustomizationUeId,
            type: 'IMAGE',
            ueIdentifier: selectedCustomizationUeId,
            isActive: true,
            data: dataPayload,
          },
        );
      }

      const refreshed = await EventsService.getRoomSpots(currentRoom.roomId);
      setRoomSpots(refreshed);

      const updatedSpot =
        refreshed.find(
          (spot: any) => spot.ueIdentifier === selectedCustomizationUeId,
        ) || null;

      if (updatedSpot) {
        sendMessageToUnreal({
          action: 'SPOT_UPDATED',
          spot: updatedSpot,
        });
      }

      setUploadedAsset(uploadInfo);
      setShowCustomSpotsEditor(false);
      setSelectedCustomizationUeId(null);
    } catch (uploadError) {
      setError(
        t('customSpotsEditor.errorUpload', 'Failed to upload file, please try again.'),
      );
    } finally {
      setIsUploading(false);
    }
  }, [
    currentRoom,
    roomSpots,
    selectedCustomizationUeId,
    selectedFile,
    sendMessageToUnreal,
    setRoomSpots,
    setSelectedCustomizationUeId,
    setShowCustomSpotsEditor,
    t,
  ]);

  const handleClose = () => {
    resetState();
    setSelectedCustomizationUeId(null);
    setShowCustomSpotsEditor(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: classes.paper,
      }}
    >
      <DialogTitle sx={classes.title}>
        {t('customSpotsEditor.title', 'Custom Spots Editor')}
        <IconButton sx={classes.closeButton} onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={classes.content}>
        <Box
          sx={{
            ...classes.dropZone,
            ...(isDragging ? classes.dropZoneActive : {}),
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <UploadFileIcon sx={classes.uploadIcon} />
          <Typography variant="h6" sx={classes.dropZoneTitle}>
            {t('customSpotsEditor.dropTitle', 'Drag and drop an image')}
          </Typography>
          <Typography variant="body2" sx={classes.dropZoneSubtitle}>
            {t('customSpotsEditor.dropSubtitle', 'or choose one manually')}
          </Typography>
          <Button
            variant="contained"
            onClick={handleSelectClick}
            disabled={isUploading}
            sx={classes.selectButton}
          >
            {t('customSpotsEditor.chooseFile', 'Choose file')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => handleFiles(event.target.files)}
          />
        </Box>

        {previewUrl ? (
          <Box sx={classes.previewRow}>
            <Typography sx={classes.statusText}>
              {t('customSpotsEditor.previewTitle', 'Preview')}
            </Typography>
            <Box component="img" src={previewUrl} alt="preview" sx={classes.previewImage} />
            <Typography sx={classes.previewText}>{selectedFile?.name}</Typography>
          </Box>
        ) : null}

        {uploadedAsset ? (
          <Box sx={classes.previewRow}>
            <Typography sx={classes.statusText}>
              {t('customSpotsEditor.uploaded', 'Uploaded')}: {uploadedAsset.name}
            </Typography>
            <Typography sx={classes.previewText}>
              {uploadedAsset.url}
            </Typography>
          </Box>
        ) : null}

        {error ? (
          <Typography sx={classes.errorText} variant="body2">
            {error}
          </Typography>
        ) : null}

        {isUploading ? (
          <Box sx={classes.statusRow}>
            <CircularProgress size={20} />
            <Typography sx={classes.statusText}>
              {t(
                'customSpotsEditor.uploading',
                'Uploading file and updating spot...',
              )}
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedFile || isUploading}
          onClick={handleConfirm}
          sx={classes.confirmButton}
        >
          {t('customSpotsEditor.confirm', 'Confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomSpotsEditor;

